import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useTranslation } from '../locales/useTranslation'
import MapView from '../components/map/MapView'
import ChargerDetail from '../components/map/ChargerDetail'
import MapSearch from '../components/map/MapSearch'

const MapPage = ({ session, showToast, setActiveTab, mapFocus, requireAuth }) => {
  const { t } = useTranslation()
  const [chargers, setChargers] = useState([])
  const [filtered, setFiltered] = useState([])
  const [selected, setSelected] = useState(null)
  const [loading, setLoading] = useState(true)
  const [filterAvail, setFilterAvail] = useState(false)
  const [search, setSearch] = useState('')
  const [locationTarget, setLocationTarget] = useState(null)

  const fetchChargers = useCallback(async () => {
    try {
      // Public query — no auth needed
      const { data, error } = await supabase
        .from('chargers')
        .select(`*, charger_availability (day_of_week, start_time, end_time)`)
        .eq('status', 'actief')

      if (error) throw error
      setChargers(data || [])

      // Only log event if user is logged in
      if (session) {
        await supabase.from('events').insert({
          user_id: session.user.id,
          event_type: 'map_viewed',
          entity_type: 'charger',
          metadata: { charger_count: data?.length || 0 }
        })
      }
    } catch (error) {
      showToast(t('fout'), true)
    } finally {
      setLoading(false)
    }
  }, [session, showToast, t])

  const applyFilters = useCallback(() => {
    let result = [...chargers]
    if (search) {
      const q = search.toLowerCase()
      result = result.filter(c =>
        c.name?.toLowerCase().includes(q) ||
        c.address?.toLowerCase().includes(q) ||
        c.city?.toLowerCase().includes(q) ||
        c.postal_code?.toLowerCase().includes(q)
      )
    }
    if (filterAvail) {
      result = result.filter(c => c.is_available)
    }
    setFiltered(result)
  }, [chargers, search, filterAvail])

  useEffect(() => { fetchChargers() }, [fetchChargers])
  useEffect(() => { applyFilters() }, [applyFilters])

  useEffect(() => {
    if (mapFocus) setSelected(mapFocus)
  }, [mapFocus])

  const handleSelect = async (charger) => {
    setSelected(charger)
    if (charger && session) {
      await supabase.from('events').insert({
        user_id: session.user.id,
        event_type: 'charger_viewed',
        entity_type: 'charger',
        entity_id: charger.id,
        metadata: { source: 'map_pin' }
      })
    }
  }

  const handleSearch = async (query) => {
    setSearch(query)
    if (query && session) {
      await supabase.from('events').insert({
        user_id: session.user.id,
        event_type: 'search_performed',
        entity_type: 'charger',
        metadata: { query, results_count: filtered.length }
      })
    }
  }

  const handleLocationSearch = async (lat, lon) => {
    setLocationTarget({ lat, lon })
    if (session) {
      await supabase.from('events').insert({
        user_id: session.user.id,
        event_type: 'search_performed',
        entity_type: 'charger',
        metadata: { type: 'location_search', lat, lon }
      })
    }
  }

  return (
    <div style={{ height: '100%', position: 'relative', overflow: 'hidden' }}>

      <MapSearch
        search={search}
        setSearch={handleSearch}
        filterAvail={filterAvail}
        setFilterAvail={setFilterAvail}
        count={filtered.length}
        availableCount={filtered.filter(c => c.is_available).length}
        onLocationSearch={handleLocationSearch}
      />

      {loading ? (
        <div style={{
          height: '100%', display: 'flex', alignItems: 'center',
          justifyContent: 'center', background: 'var(--cream)',
          color: 'var(--green)', fontWeight: 600
        }}>
          {t('laden')}
        </div>
      ) : (
        <MapView
          chargers={filtered}
          selected={selected}
          onSelect={handleSelect}
          locationTarget={locationTarget}
        />
      )}

      {selected && (
        <ChargerDetail
          charger={selected}
          session={session}
          onClose={() => setSelected(null)}
          showToast={showToast}
          requireAuth={requireAuth}
        />
      )}

      {/* Add charger button — only when logged in */}
      {session && (
        <button
          onClick={() => setActiveTab('laadpalen')}
          style={{
            position: 'absolute',
            bottom: selected ? 280 : 20,
            left: 16, zIndex: 15,
            background: 'var(--green)', border: 'none',
            borderRadius: 14, padding: '11px 16px',
            color: 'white', fontWeight: 700, fontSize: 13,
            cursor: 'pointer', display: 'flex',
            alignItems: 'center', gap: 7,
            boxShadow: '0 4px 20px rgba(61,158,95,0.4)',
            transition: 'bottom 0.3s ease',
            fontFamily: 'DM Sans, sans-serif'
          }}>
          ⚡ Laadpaal toevoegen
        </button>
      )}
    </div>
  )
}

export default MapPage
